// Die Fenster der Kasse: Kunde zuordnen, freier Betrag, bezahlen.
import { useMemo, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { euro, profil } from '../../../gemeinsam/lib/daten';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Fenster } from '../../../gemeinsam/komponenten/Fenster';
import { Auswahl, Feld, Hinweis, Knopf } from '../../../gemeinsam/komponenten/Basis';
import Icon from '../../../gemeinsam/komponenten/Icon';
import type { Kunde, Rechnung } from '../../../gemeinsam/lib/typen';
import { belegOeffnen } from '../lib/drucken';
import { nettoAusBrutto, neueId, positionenFuerApi, schnellbetraege, type Bon, type Summen, type Zeile } from '../lib/kasse';

/** „1.234,50" und „59.50" — mit Komma ist der Punkt Tausendertrenner, sonst Dezimalpunkt. */
export const zahlEingabe = (s: string) => {
  const t = String(s).trim();
  return Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t) || 0;
};
const r2 = (n: number) => Math.round(n * 100) / 100;

export function KundeWaehlen({ kunden, beiWahl, beiSchliessen }: {
  kunden: Kunde[]; beiWahl: (k: Kunde | null) => void; beiSchliessen: () => void;
}) {
  const { t } = useSprache();
  const [suche, setSuche] = useState('');
  const treffer = useMemo(() => {
    const s = suche.trim().toLowerCase();
    return kunden.filter(k => !s || k.name.toLowerCase().includes(s) || (k.kundennummer || '').toLowerCase().includes(s) || (k.ort || '').toLowerCase().includes(s)).slice(0, 50);
  }, [kunden, suche]);
  return (
    <Fenster titel={t('kasse.kundeWaehlen')} unterzeile={t('kasse.historieHinweis')} beiSchliessen={beiSchliessen} breite="sm:max-w-md">
      <div className="space-y-3 p-4">
        <Feld label={t('kasse.kundeSuchen')} value={suche} autoFocus placeholder={t('kasse.kundeSuche')} onChange={e => setSuche(e.target.value)} />
        <ul className="max-h-[50vh] divide-y divide-slate-200 overflow-y-auto rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
          <li>
            <button type="button" onClick={() => beiWahl(null)} className="flex w-full items-center gap-3 px-4 py-3 text-start hover:bg-slate-50 dark:hover:bg-slate-800/50">
              <span className="flex-1 font-medium text-slate-900 dark:text-slate-100">{t('kasse.laufkunde')}</span>
              <span className="text-xs text-slate-500">{t('kasse.ohneKonto')}</span>
            </button>
          </li>
          {treffer.map(k => (
            <li key={k.id}>
              <button type="button" onClick={() => beiWahl(k)} className="flex w-full items-center gap-3 px-4 py-3 text-start hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-slate-900 dark:text-slate-100">{k.name}
                    {k.kundennummer && <span className="ms-2 text-xs font-normal text-slate-500">#{k.kundennummer}</span>}</span>
                  {k.ort && <span className="block truncate text-sm text-slate-500 dark:text-slate-400">{k.ort}</span>}
                </span>
                {!!k.guthaben && k.guthaben > 0 && <span className="text-sm text-emerald-600 dark:text-emerald-400">{euro(k.guthaben)}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Fenster>
  );
}

/** Eine Zeile ohne Artikel: Dienstleistung, Pfand, Sonstiges — Betrag so, wie der Kunde ihn zahlt. */
export function FreierBetrag({ beiFertig, beiSchliessen }: { beiFertig: (z: Zeile) => void; beiSchliessen: () => void }) {
  const { t } = useSprache();
  const p = profil();
  const [text, setText] = useState('');
  const [betrag, setBetrag] = useState('');
  const [satz, setSatz] = useState(String(p.standardSteuersatz));
  const b = zahlEingabe(betrag);
  function fertig(e: React.FormEvent) {
    e.preventDefault();
    if (!(b > 0)) return;
    const s = Number(satz);
    beiFertig({
      id: neueId(), produktId: null, bezeichnung: text.trim() || t('kasse.freiStandard'), einheit: 'Stück',
      menge: 1, preis: nettoAusBrutto(b, s), steuersatz: s, rabatt: 0, teilbar: false, bestand: null,
    });
  }
  return (
    <Fenster titel={t('kasse.freierBetrag')} unterzeile={t('kasse.freiHinweis')} beiSchliessen={beiSchliessen} breite="sm:max-w-sm">
      <form onSubmit={fertig} className="space-y-3 p-4">
        <Feld label={t('kasse.betragBrutto')} value={betrag} inputMode="decimal" autoFocus required onChange={e => setBetrag(e.target.value)} />
        <Feld label={t('kasse.wofuer')} value={text} maxLength={200} placeholder={t('kasse.freiStandard')} onChange={e => setText(e.target.value)} />
        <Auswahl label={p.steuerName} value={satz} onChange={e => setSatz(e.target.value)}>
          {p.steuersaetze.map(s => <option key={s} value={s}>{s} %</option>)}
        </Auswahl>
        <Knopf type="submit" variante="primaer" className="w-full" disabled={!(b > 0)}>{t('aktion.hinzufuegen')} · {euro(b)}</Knopf>
      </form>
    </Fenster>
  );
}

type Weg = 'bar' | 'karte' | 'geteilt' | 'rechnung';

/**
 * Bezahlen: vier Wege, ein Fenster. Danach zeigt dasselbe Fenster das
 * Ergebnis — Rückgeld groß, Beleg drucken, nächster Kunde.
 */
export function Bezahlen({ bon, summe, kunden, beiKunde, beiFertig, beiSchliessen }: {
  bon: Bon; summe: Summen; kunden: Kunde[];
  beiKunde: (k: Kunde | null) => void;
  /** Verkauf gebucht — der Bon darf weg. */
  beiFertig: () => void;
  beiSchliessen: () => void;
}) {
  const { t } = useSprache();
  const kunde = kunden.find(k => k.id === bon.kundeId) ?? null;
  const [weg, setWeg] = useState<Weg>('bar');
  const [gegeben, setGegeben] = useState('');
  const [barAnteil, setBarAnteil] = useState('');
  const [anzahlung, setAnzahlung] = useState('');
  const [anzahlungArt, setAnzahlungArt] = useState<'bar' | 'karte'>('bar');
  const [ziel, setZiel] = useState('kunde');
  const [kundeWahl, setKundeWahl] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<{ beleg: Rechnung; rueckgeld: number } | null>(null);

  const gesamt = summe.brutto;
  const gegebenBetrag = zahlEingabe(gegeben);
  const rueckgeld = weg === 'bar' && gegebenBetrag > gesamt ? r2(gegebenBetrag - gesamt) : 0;
  const bar = Math.min(zahlEingabe(barAnteil), gesamt);
  const karteRest = r2(gesamt - bar);
  const anz = Math.min(zahlEingabe(anzahlung), gesamt);

  const kannBuchen = weg === 'bar' ? (gegeben.trim() === '' || gegebenBetrag >= gesamt - 0.004)
    : weg === 'karte' ? true
      : weg === 'geteilt' ? bar > 0 && karteRest > 0
        : !!kunde && anz < gesamt;

  async function buchen() {
    if (!kannBuchen || sendet) return;
    setSendet(true); setFehler(null);
    const grund = {
      kundeId: kunde?.id || undefined,
      kundeName: kunde ? undefined : 'Barverkauf',
      positionen: positionenFuerApi(bon, p => t('kasse.rabattText').replace('{p}', String(p))),
      rabattProzent: bon.rabatt || 0,
    };
    const daten = weg === 'bar' || weg === 'karte'
      ? { ...grund, status: 'bezahlt', zahlart: weg, zahlweg: weg }
      : weg === 'geteilt'
        ? { ...grund, status: 'gestellt', zahlweg: 'karte', gezahlt: [{ betrag: bar, zahlart: 'bar' }, { betrag: karteRest, zahlart: 'karte' }] }
        : {
          ...grund, status: 'gestellt', zahlweg: 'ueberweisung',
          gezahlt: anz > 0 ? { betrag: anz, zahlart: anzahlungArt } : undefined,
          zahlungszielTage: /^\d+$/.test(ziel) ? Number(ziel) : undefined,
        };
    try {
      const beleg = await api<Rechnung>('/invoices', { methode: 'POST', daten });
      setErgebnis({ beleg, rueckgeld });
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : t('kasse.fehlgeschlagen'));
    } finally { setSendet(false); }
  }

  async function drucken() {
    if (!ergebnis) return;
    const problem = await belegOeffnen(apiUrl(`/api/invoices/${ergebnis.beleg.id}/druck`));
    if (problem) setFehler(problem);
  }

  if (ergebnis) {
    const offen = r2(ergebnis.beleg.brutto - (ergebnis.beleg.zahlungen ?? []).reduce((s, z) => s + z.betrag, 0));
    return (
      <Fenster titel={t('kasse.abgeschlossen')} unterzeile={`${t('kasse.beleg')} ${ergebnis.beleg.nummer}`} beiSchliessen={beiFertig} breite="sm:max-w-md">
        <div className="space-y-4 p-5 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400">
            <Icon name="haken" groesse={30} />
          </span>
          {ergebnis.rueckgeld > 0 ? (
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('kasse.rueckgeldGross')}</p>
              <p className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">{euro(ergebnis.rueckgeld)}</p>
            </div>
          ) : <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{euro(ergebnis.beleg.brutto)}</p>}
          {ergebnis.beleg.status !== 'bezahlt' && (
            <Hinweis art="warnung">{t('kasse.offenBis').replace('{betrag}', euro(offen)).replace('{datum}', new Date(ergebnis.beleg.faelligAm).toLocaleDateString())}</Hinweis>
          )}
          {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
          <div className="flex gap-2">
            <Knopf className="flex-1" onClick={() => void drucken()}>{t('aktion.drucken')}</Knopf>
            <Knopf variante="primaer" className="flex-[2]" autoFocus onClick={beiFertig}>{t('kasse.naechster')}</Knopf>
          </div>
        </div>
      </Fenster>
    );
  }

  if (kundeWahl) {
    return <KundeWaehlen kunden={kunden} beiSchliessen={() => setKundeWahl(false)} beiWahl={k => { beiKunde(k); setKundeWahl(false); }} />;
  }

  const WEGE: Array<[Weg, string]> = [['bar', t('kasse.bar')], ['karte', t('kasse.karte')], ['geteilt', t('kasse.geteilt')], ['rechnung', t('kasse.aufRechnung')]];
  const schnell = schnellbetraege(gesamt);

  return (
    <Fenster titel={t('kasse.bezahlen')} beiSchliessen={beiSchliessen} breite="sm:max-w-md">
      <div className="space-y-4 p-5">
        <div className="text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">{kunde ? kunde.name : t('kasse.laufkunde')}</p>
          <p className="text-4xl font-bold tabular-nums text-slate-900 dark:text-slate-100">{euro(gesamt)}</p>
        </div>

        <div className="grid grid-cols-4 gap-1 rounded-xl border border-slate-200 p-1 dark:border-slate-700" role="radiogroup" aria-label={t('kasse.zahlart')}>
          {WEGE.map(([id, name]) => (
            <button key={id} type="button" role="radio" aria-checked={weg === id} onClick={() => { setWeg(id); setFehler(null); }}
                    className={`rounded-lg px-1 py-2 text-sm font-medium transition ${weg === id
                      ? 'bg-marke-600 text-white' : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
              {name}
            </button>
          ))}
        </div>

        {weg === 'bar' && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2">
              {schnell.map((b, i) => (
                <button key={b} type="button" onClick={() => setGegeben(String(b).replace('.', ','))}
                        className="rounded-lg border border-slate-300 px-1 py-2.5 text-sm font-semibold tabular-nums text-slate-800 hover:border-marke-400 hover:bg-marke-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-marke-950/40">
                  {i === 0 ? t('kasse.passend') : euro(b).replace(/,00(?=\s?€)/, '')}
                </button>
              ))}
            </div>
            <Feld label={t('kasse.gegebenKurz')} value={gegeben} inputMode="decimal" autoFocus placeholder={euro(gesamt)}
                  onChange={e => setGegeben(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') void buchen(); }} />
            {rueckgeld > 0 && (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-center text-lg font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                {t('kasse.rueckgeld').replace('{betrag}', euro(rueckgeld))}
              </p>
            )}
            {gegeben.trim() !== '' && gegebenBetrag < gesamt - 0.004 && <p className="text-center text-sm text-amber-700 dark:text-amber-400">{t('kasse.zuWenig').replace('{betrag}', euro(r2(gesamt - gegebenBetrag)))}</p>}
          </div>
        )}

        {weg === 'karte' && <p className="text-center text-sm text-slate-600 dark:text-slate-400">{t('kasse.karteHinweis')}</p>}

        {weg === 'geteilt' && (
          <div className="space-y-2">
            <Feld label={t('kasse.barAnteil')} value={barAnteil} inputMode="decimal" autoFocus onChange={e => setBarAnteil(e.target.value)} />
            <div className="flex justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800/60">
              <span className="text-slate-600 dark:text-slate-400">{t('kasse.karteRest')}</span>
              <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">{euro(Math.max(0, karteRest))}</span>
            </div>
          </div>
        )}

        {weg === 'rechnung' && (
          <div className="space-y-3">
            {kunde ? (
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
                <span className="font-medium text-slate-900 dark:text-slate-100">{kunde.name}</span>
                <Knopf variante="still" className="!py-1 !text-xs" onClick={() => setKundeWahl(true)}>{t('kasse.kundeWechseln')}</Knopf>
              </div>
            ) : (
              <Hinweis art="warnung">
                {t('kasse.kundePflicht')}{' '}
                <button type="button" onClick={() => setKundeWahl(true)} className="font-semibold underline">{t('kasse.kundeWaehlen')}</button>
              </Hinweis>
            )}
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <Feld label={t('kasse.anzahlung')} value={anzahlung} inputMode="decimal" placeholder="0,00" onChange={e => setAnzahlung(e.target.value)} />
              <Auswahl label={t('kasse.zahlart')} value={anzahlungArt} onChange={e => setAnzahlungArt(e.target.value as 'bar' | 'karte')}>
                <option value="bar">{t('kasse.bar')}</option>
                <option value="karte">{t('kasse.karte')}</option>
              </Auswahl>
            </div>
            <Auswahl label={t('rechnungen.zahlungsziel')} value={ziel} onChange={e => setZiel(e.target.value)}>
              <option value="kunde">{t('rechnungen.zielKunde').replace('{tage}', String(kunde?.zahlungszielTage ?? 14))}</option>
              <option value="0">{t('rechnungen.zielSofort')}</option>
              {[7, 14, 30, 60].map(n => <option key={n} value={String(n)}>{t('rechnungen.zielTage').replace('{tage}', String(n))}</option>)}
            </Auswahl>
          </div>
        )}

        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

        <Knopf variante="primaer" className="w-full !py-3 !text-base" laedt={sendet} disabled={!kannBuchen} onClick={() => void buchen()}>
          {weg === 'bar' ? t('kasse.barKassieren').replace('{betrag}', euro(gesamt))
            : weg === 'karte' ? t('kasse.karteBezahlt').replace('{betrag}', euro(gesamt))
              : weg === 'geteilt' ? t('kasse.geteiltKassieren').replace('{bar}', euro(bar)).replace('{karte}', euro(Math.max(0, karteRest)))
                : t('kasse.rechnungBuchen').replace('{offen}', euro(r2(gesamt - anz)))}
        </Knopf>
      </div>
    </Fenster>
  );
}
